import LINKS from "@/lib/links";
import { Component } from "@builder.io/sdk";

export const headerConfig: Component = {
  name: "Header",
  inputs: [
    {
      name: "id",
      type: "string",
      defaultValue: "header",
      helperText:
        "Unique identifier for the section. Useful for linking to the section by '#id'.",
      advanced: true,
    },
    {
      name: "headingClassName",
      type: "string",
      helperText: "Tailwind CSS classes for the heading",
    },
    {
      name: "heading",
      type: "string",
      friendlyName: "Heading",
      defaultValue: "Services for Families",
      helperText: "Main heading for the header section",
      required: true,
    },
    {
      name: "subheading",
      type: "longText",
      friendlyName: "Subheading",
      defaultValue:
        "Find peace of mind and exceptional care for your loved ones, wherever they call home.",
      helperText: "Subheading text below the main heading",
      required: true,
    },
    {
      name: "description",
      type: "richText",
      defaultValue:
        "Care Indeed's Services for Families provides compassionate, professional support tailored to meet the unique needs of each family.",
      helperText: "Detailed description of the service",
      required: true,
    },
    {
      name: "showPrimaryCTA",
      type: "boolean",
      defaultValue: true,
      friendlyName: "Show Primary Button",
      helperText: "Do you want to show the primary button?",
    },
    {
      name: "primaryCTA",
      friendlyName: "Primary Button",
      type: "object",
      showIf: "options.get('showPrimaryCTA') === true",

      subFields: [
        {
          name: "className",
          type: "string",
          helperText: "Tailwind CSS classes for the button",
        },
        {
          name: "color",
          type: "string",
          enum: ["orange", "teal"],
          friendlyName: "Button Color",
          helperText: "Color for the primary button",
        },
        {
          name: "children",
          type: "string",
          friendlyName: "Button Text",
          helperText: "Text for the primary button",
          required: true,
        },
        {
          name: "href",
          friendlyName: "Button Link",
          type: "string",
          helperText: "Link for the primary button",
          required: true,
        },
      ],
      defaultValue: {
        color: "orange",
        children: "Get Care Now",
        href: "/care-assessment",
      },
    },
    {
      name: "showSecondaryCTA",
      type: "boolean",
      defaultValue: true,
      friendlyName: "Show Secondary Button",
      helperText: "Do you want to show the secondary button?",
    },
    {
      name: "secondaryCTA",
      friendlyName: "Secondary Button",
      type: "object",
      showIf: "options.get('showSecondaryCTA') === true",
      subFields: [
        {
          name: "className",
          type: "string",
          helperText: "Tailwind CSS classes for the button",
        },
        {
          name: "color",
          type: "string",
          enum: ["orange", "teal"],
          friendlyName: "Button Color",
          helperText: "Color for the secondary button",
        },
        {
          name: "children",
          type: "string",
          friendlyName: "Button Text",
          helperText: "Text for the secondary button",
          required: true,
        },
        {
          name: "href",
          type: "string",
          friendlyName: "Button Link",
          helperText: "Link for the secondary button",
          required: true,
        },
      ],
      defaultValue: {
        color: "teal",
        children: "Learn more",
        href: "/inquiry-form",
      },
    },
    {
      name: "showAnchors",
      type: "boolean",
      defaultValue: true,
      friendlyName: "Show Anchors",
      helperText: "Do you want to show the anchors?",
    },
    {
      name: "anchors",
      friendlyName: "Anchors",
      helperText: "Anchor links for the page",
      type: "object",
      showIf: "options.get('showAnchors') === true",
      subFields: [
        {
          name: "items",
          type: "list",
          friendlyName: "Anchor Links",
          helperText: "Anchor links for the page",
          subFields: [
            {
              name: "title",
              friendlyName: "Display Name",
              type: "string",
              helperText: "Display name for the anchor link",
              required: true,
            },
            {
              name: "href",
              friendlyName: "Link Destination",
              type: "string",
              helperText: "Anchor link destination",
              required: true,
            },
          ],
        },
      ],
      defaultValue: {
        items: [
          {
            title: "About Services for Famliies",
            href: "#about",
          },
          {
            title: "Our Care Solutions",
            href: "#our-care-solutions",
          },
          {
            title: "Benefits",
            href: "#benefits",
          },
        ],
      },
    },
    {
      name: "breadcrumbs",
      friendlyName: "Breadcrumbs",
      helperText: "Breadcrumb navigation for the page",
      type: "object",
      subFields: [
        {
          name: "data",
          friendlyName: "Breadcrumb",
          type: "list",
          subFields: [
            {
              name: "name",
              friendlyName: "Display Name",
              type: "string",
              helperText: "Display name for the breadcrumb",
              required: true,
            },
            {
              name: "href",
              friendlyName: "Link",
              type: "string",
              helperText: "Link for the breadcrumb",
              required: true,
            },
          ],
          defaultValue: [
            {
              href: "/families",
              name: "Services for Families",
            },
          ],
        },
      ],
      defaultValue: {
        data: [
          {
            href: LINKS.servicesForFamilies.href,
            name: LINKS.servicesForFamilies.title,
          },
        ],
      },
    },
    {
      name: "image",
      friendlyName: "Header Image",
      type: "object",
      required: true,
      defaultValue: {
        src: "https://cdn.builder.io/api/v1/image/assets%2Fad5264505ff74b8ba929a635f7c3c34f%2F19988d64182f4b8c976725860ea5c6fe?width=500&height=500",
        alt: "Header Image",
      },
      subFields: [
        {
          name: "src",
          type: "file",
          allowedFileTypes: ["jpeg", "png"],
          helperText: "Header image file",
        },
        {
          name: "alt",
          type: "string",
        },
      ],
    },
  ],
};
